import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRect } from "@reach/rect";
import classNames from "classnames";
import dynamic from "next/dynamic";
import { memo, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { AnnotationsAvatarsContainer } from "~/components/AnnotationsV2/AnnotationsAvatarsContainer/AnnotationsAvatarsContainer";
import { AnnotationsKeyShortcuts } from "~/components/AnnotationsV2/components/AnnotationsKeyShortcuts";
import { useAnnotationV2Context } from "~/components/AnnotationsV2/providers/AnnotationV2Provider";
import { annotationTooltipText } from "~/components/AnnotationsV2/shared/constants";
import { useFullscreenContext } from "~/components/AssetModal/shared/context/FullscreenContext";
import { AnnotationTooltip } from "~/components/AssetModalV2/Visualizer/Annotations/AnnotationTooltip";
import { useMouseTooltip } from "~/components/AssetModalV2/Visualizer/Annotations/useMouseTooltip";
import { BOUNDING_BOX_LAYER_Z_INDEX } from "~/components/BoundingBox/constants";
import { QueryParamNames } from "~/constants/search";
import { ANNOTATION_LAYER } from "~/constants/testIDs";
import { activeTimestampParamSelector } from "~/store/router/selectors";
import { isCypressRun, setQueryParams } from "~/utils/PathUtils";
// Dynamic imports to avoid SSR issues with react-konva
const DiscussionAnnotationsDisplay = dynamic(()=>import("~/components/AnnotationsV2/components/DiscussionAnnotationsDisplay").then((mod)=>mod.DiscussionAnnotationsDisplay), {
    loadableGenerated: {
        webpack: ()=>[
                require.resolveWeak("~/components/AnnotationsV2/components/DiscussionAnnotationsDisplay")
            ]
    },
    ssr: false
});
const MultiAnnotationLayer = dynamic(()=>import("~/components/AnnotationsV2/components/MultiAnnotationLayer").then((mod)=>mod.MultiAnnotationLayer), {
    loadableGenerated: {
        webpack: ()=>[
                require.resolveWeak("~/components/AnnotationsV2/components/MultiAnnotationLayer")
            ]
    },
    ssr: false
});
/**
 * V2: Annotations component that supports multiple annotations per discussion
 * Uses AnnotationsAvatarsContainer for rendering discussions with multiple annotations
 */ export const AnnotationsV2 = /*#__PURE__*/ memo((param)=>{
    let { keyShortcutsEnabled, canDrawAnnotation, pageNumber, onIsSelectingChange, topOffset = 0, hideAnnotations, width, height } = param;
    // Suppress unused variable warning - this prop is part of the interface but not used in our V2 implementation
    void onIsSelectingChange;
    const { isFullscreen } = useFullscreenContext();
    const containerRef = useRef(null);
    const annotationTooltipRef = useRef(null);
    const containerRect = useRect(containerRef);
    const { annotationType, annotations, isAnnotationsEnabled } = useAnnotationV2Context();
    const activeTimestamp = useSelector(activeTimestampParamSelector);
    // Use passed dimensions directly (no more useResizeDetector issues)
    const containerWidth = width || 0;
    const containerHeight = height || 0;
    // Check if there are annotations for the current page
    const pageAnnotations = useMemo(()=>{
        return annotations.filter((a)=>{
            if (a.type === "box") return a.pageNumber === pageNumber;
            if (a.type === "pencil") return a.lines.some((line)=>line.pageNumber === pageNumber);
            return false;
        });
    }, [
        annotations,
        pageNumber
    ]);
    useMouseTooltip({
        // This complains about Ref types but it works as expected
        // @ts-ignore
        containerRef: containerRef,
        tooltipRef: annotationTooltipRef,
        disabled: !canDrawAnnotation || pageAnnotations.length > 0,
        dependencies: [
            pageNumber
        ]
    });
    const isDisabled = !canDrawAnnotation || !isAnnotationsEnabled;
    const annotationLayer = useMemo(()=>{
        var _containerRect_left;
        // Use MultiAnnotationLayer for box annotations
        return /*#__PURE__*/ _jsx(MultiAnnotationLayer, {
            containerWidth: containerWidth !== null && containerWidth !== void 0 ? containerWidth : 0,
            containerHeight: containerHeight !== null && containerHeight !== void 0 ? containerHeight : 0,
            containerLeft: (_containerRect_left = containerRect === null || containerRect === void 0 ? void 0 : containerRect.left) !== null && _containerRect_left !== void 0 ? _containerRect_left : 0,
            containerRef: containerRef,
            disabled: isDisabled,
            pageNumber: pageNumber
        });
    }, [
        containerHeight,
        containerRect === null || containerRect === void 0 ? void 0 : containerRect.left,
        containerWidth,
        isDisabled,
        pageNumber
    ]);
    const onAvatarClick = useCallback((discussion)=>{
        setQueryParams({
            [QueryParamNames.discussionId]: [
                discussion.id
            ]
        }, "replace");
    }, []);
    if (isFullscreen) {
        return null;
    }
    var _containerRect_left, _containerRect_top;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": ANNOTATION_LAYER,
                ref: containerRef,
                className: classNames("absolute left-1/2 max-w-full -translate-x-1/2 -translate-y-1/2 transform", isDisabled ? "[&>canvas]:pointer-events-none" : annotationType === "box" ? "[&>canvas]:cursor-box-annotation" : "[&>canvas]:cursor-pencil-annotation"),
                style: {
                    zIndex: BOUNDING_BOX_LAYER_Z_INDEX,
                    height: containerHeight,
                    width: containerWidth,
                    minWidth: containerWidth,
                    minHeight: containerHeight,
                    top: "calc(50% + ".concat(topOffset, "px)"),
                    pointerEvents: isDisabled ? "none" : "auto"
                },
                children: [
                    !hideAnnotations && /*#__PURE__*/ _jsx("div", {
                        style: {
                            position: "absolute",
                            inset: 0,
                            zIndex: 1
                        },
                        children: /*#__PURE__*/ _jsx(DiscussionAnnotationsDisplay, {
                            containerWidth: containerWidth,
                            containerHeight: containerHeight,
                            pageNumber: pageNumber,
                            highlightMode: "bright",
                            showAllAnnotations: false
                        })
                    }),
                    !hideAnnotations && /*#__PURE__*/ _jsx("div", {
                        style: {
                            position: "absolute",
                            inset: 0,
                            zIndex: 2
                        },
                        children: isAnnotationsEnabled || pageAnnotations.length > 0 ? annotationLayer : null
                    }),
                    !!containerWidth && !!containerHeight && !hideAnnotations && /*#__PURE__*/ _jsx("div", {
                        className: "pointer-events-none absolute inset-0",
                        style: {
                            zIndex: 3
                        },
                        children: /*#__PURE__*/ _jsx(AnnotationsAvatarsContainer, {
                            containerWidth: containerWidth,
                            containerHeight: containerHeight,
                            containerLeft: (_containerRect_left = containerRect === null || containerRect === void 0 ? void 0 : containerRect.left) !== null && _containerRect_left !== void 0 ? _containerRect_left : 0,
                            containerTop: (_containerRect_top = containerRect === null || containerRect === void 0 ? void 0 : containerRect.top) !== null && _containerRect_top !== void 0 ? _containerRect_top : 0,
                            timestamp: activeTimestamp,
                            pageNumber: pageNumber,
                            onAvatarClick: onAvatarClick,
                            hideAnnotations: pageAnnotations.length > 0 || hideAnnotations
                        })
                    }),
                    isAnnotationsEnabled && annotationType && pageAnnotations.length === 0 && /*#__PURE__*/ _jsx(AnnotationTooltip, {
                        text: annotationTooltipText[annotationType],
                        innerRef: annotationTooltipRef,
                        shouldDisplay: canDrawAnnotation
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(AnnotationsKeyShortcuts, {
                enabled: keyShortcutsEnabled
            }),
            !!containerWidth && !!containerHeight && isCypressRun() && /*#__PURE__*/ _jsx("div", {
                "data-testid": "CYPRESS_ANNOTATIONS_READY"
            })
        ]
    });
});
AnnotationsV2.displayName = "AnnotationsV2";
